/**
 * Manage Insights — the marketplace: the catalog (packs/index.json) the
 * insight-packs repo publishes to this environment's bucket, each pack
 * graded against the registry, with Install for platform admins.
 *
 * Installing is the only way a pack is registered (app/insight_packs/
 * marketplace.py): the server checks the bundle's bytes against the
 * index, then runs registration's validation on them. Nothing installs by
 * itself.
 */
import { useCallback, useEffect, useState } from 'react'

import { ApiError } from '../lib/api'
import {
  importRelease,
  listMarketplace,
  type Marketplace,
  type MarketplacePack,
  type MarketplaceVersion,
  type PackState,
  type VersionStatus,
} from '../packs/promotions'

const STATE_LABEL: Record<PackState, string> = {
  installed: 'installed',
  update: 'update available',
  older: 'installed version is newer',
  conflict: 'conflict',
  new: 'not installed',
  broken: 'broken',
}

const VERSION_LABEL: Record<VersionStatus, string> = {
  available: '',
  installed: 'installed',
  conflict: 'conflict',
  broken: 'broken',
}

const CONFLICT_TIP = 'This name@version is registered here with different bytes. '
  + 'It is left as it is; remove that row to install the published one.'

const msg = (e: unknown) => (e instanceof ApiError ? e.message : String(e))

function published(iso: string | null | undefined): string | null {
  if (!iso) return null
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? null : at.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

export default function LabMarketplace({ canInstall, onImported }: {
  /** Platform admins install; everyone else with access reads. */
  canInstall: boolean
  onImported: () => void
}) {
  const [market, setMarket] = useState<Marketplace | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [installError, setInstallError] = useState<string | null>(null)

  // refresh: revalidate the index now rather than the server's 120 s cache.
  // An install needs no refresh: states are graded fresh on every call.
  const load = useCallback((refresh = false) => {
    setError(null)
    listMarketplace(refresh)
      .then(setMarket)
      .catch((e: unknown) => setError(msg(e)))
  }, [])

  useEffect(() => { load() }, [load])

  const install = (name: string, version: string) => {
    const key = `${name}@${version}`
    setBusy(key)
    setNotice(null)
    setInstallError(null)
    importRelease(name, version)
      .then((res) => {
        setNotice(res.imported ? `Installed ${key}.` : `${key} was already installed.`)
        load()
        onImported()
      })
      .catch((e: unknown) => setInstallError(`${key}: ${msg(e)}`))
      .finally(() => setBusy(null))
  }

  const versionRow = (p: MarketplacePack, v: MarketplaceVersion) => {
    const key = `${p.name}@${v.version}`
    return (
      <div key={v.version}>
        {v.version}
        {published(v.published_at) && (
          <span className="muted" title={`Published ${v.published_at}`}>
            {' '}· {published(v.published_at)}
          </span>
        )}
        {VERSION_LABEL[v.status] && (
          <span className="muted"
                title={v.status === 'conflict' ? CONFLICT_TIP : undefined}>
            {' '}· {VERSION_LABEL[v.status]}
          </span>
        )}
        {v.status === 'broken' && v.error && (
          <div className="muted">{v.error}</div>
        )}
        {canInstall && v.status === 'available' && (
          <>
            {' '}
            <button className="linklike" disabled={busy !== null}
                    aria-label={`install ${key}`}
                    onClick={() => install(p.name, v.version)}>
              {busy === key ? 'installing…' : 'install'}
            </button>
          </>
        )}
      </div>
    )
  }

  // The latest version is the one on offer. Older ones fold away so the
  // page does not invite installing them; the summary still names any
  // older version that is installed or in conflict, so nothing is hidden.
  const versionsCell = (p: MarketplacePack) => {
    const head = p.versions.find((v) => v.version === p.latest) ?? p.versions[0]
    const older = p.versions.filter((v) => v !== head)
    const notable = older.filter((v) => v.status === 'installed' || v.status === 'conflict')
    return (
      <div className="mono">
        {head && versionRow(p, head)}
        {older.length > 0 && (
          <details className="mkt-older">
            <summary>
              {older.length} older version{older.length === 1 ? '' : 's'}
              {notable.length > 0 && (
                <> · {notable.map((v) => `${v.version} ${VERSION_LABEL[v.status]}`).join(', ')}</>
              )}
            </summary>
            {older.map((v) => versionRow(p, v))}
          </details>
        )}
      </div>
    )
  }

  let body
  if (error) {
    body = <div className="lab-error">Could not read the marketplace — {error}</div>
  } else if (!market) {
    body = <p className="muted">Loading…</p>
  } else if (!market.configured) {
    body = <div className="lab-empty">No marketplace is configured on this environment.</div>
  } else if (market.index_missing) {
    body = <div className="lab-empty">Nothing has been published to this environment yet (no packs/index.json).</div>
  } else if (market.packs.length === 0) {
    body = <div className="lab-empty">The catalog lists no packs.</div>
  } else {
    body = (
      <table className="lab-table">
        <thead>
          <tr>
            <th>Pack</th><th>Title &amp; description</th><th>Latest</th>
            <th>Versions</th><th>State</th>
          </tr>
        </thead>
        <tbody>
          {market.packs.map((p) => (
            <tr key={p.name}>
              <td className="mono">{p.name}</td>
              <td>
                {p.title && <div className="row-title">{p.title}</div>}
                {p.description ?? '—'}
              </td>
              <td className="num">{p.latest ?? '—'}</td>
              <td>{versionsCell(p)}</td>
              <td>
                <span title={p.state === 'conflict' ? CONFLICT_TIP
                  : p.state === 'older' ? `Installed: ${p.installed_versions.join(', ')}. The catalog's latest is older; installing it would be a downgrade.`
                  : undefined}>
                  {STATE_LABEL[p.state]}
                </span>
                {p.state === 'broken' && p.error && <div className="muted">{p.error}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <section>
      <div className="section-head">
        <h2>Marketplace</h2>
        <button className="linklike" onClick={() => load(true)}>refresh</button>
      </div>
      <p className="lab-sub" style={{ marginBottom: 'var(--space-2)' }}>
        Packs published by the insight-packs repo
        {market?.bucket ? <> to <span className="mono">{market.bucket}</span></> : null}.
        Install checks a version&apos;s bytes against the catalog, then registers it.
        {market?.generated_at && (
          <> Catalog generated {new Date(market.generated_at).toLocaleString()}.</>
        )}
      </p>
      {installError && <div className="lab-error">{installError}</div>}
      {notice && <div className="lab-notice" role="status">{notice}</div>}
      {body}
    </section>
  )
}
